import React, { useState } from "react";
import { IoClose, IoGiftOutline } from "react-icons/io5";
import { FiCheckCircle } from "react-icons/fi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import toast from "react-hot-toast";
import ClipLoader from "react-spinners/ClipLoader";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import { errorMessage } from "../../utils/errorMessage";

type InviteCodePreview = {
  status: string;
  data: {
    code: {
      depositReward: number;
      tradeReward: number;
      minDepositUSD: number;
      minTradeUSD: number;
    };
    alreadyJoined: boolean;
    eligible: boolean;
    reason: string | null;
  };
};

const InviteCodeModal = ({
  setOpenInviteCode,
}: {
  setOpenInviteCode: (open: boolean) => void;
}) => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();
  const [inviteCode, setInviteCode] = useState("");
  const [debouncedCode] = useDebounce(inviteCode.trim(), 600);

  const { data: preview, isFetching, isSuccess, isError } = useQuery({
    queryKey: ["invite-code-preview", debouncedCode],
    queryFn: async () => {
      const response = await axiosInstance.get<InviteCodePreview>(
        API.previewInviteCode(debouncedCode)
      );
      return response.data;
    },
    enabled: debouncedCode.length > 0,
    retry: false,
  });

  const trimmedCode = inviteCode.trim();
  const isTyping = trimmedCode !== debouncedCode;
  const showInputLoader = trimmedCode.length > 0 && (isTyping || isFetching);
  const showInputSuccess =
    !showInputLoader &&
    isSuccess &&
    preview?.status === "success" &&
    trimmedCode === debouncedCode;

  const previewData =
    showInputSuccess && preview?.data?.code ? preview.data : null;

  const { mutate: redeemCode, isPending: isRedeeming } = useMutation({
    mutationFn: async (code: string) => {
      const response = await axiosInstance.post(API.redeemInviteCode, { code });
      return response.data;
    },
    onSuccess: (response: { message?: string }) => {
      toast.success(response?.message || "Invite code applied successfully");
      queryClient.invalidateQueries({ queryKey: ["invite-code-joined"] });
      setOpenInviteCode(false);
    },
    onError: (error: any) => {
      toast.error(
        errorMessage(error?.message || error?.data?.message || "Failed to apply invite code")
      );
    },
  });

  const handleApply = () => {
    if (!previewData) return;

    if (!previewData.eligible || previewData.alreadyJoined) {
      toast.error(
        previewData.reason || "You are not eligible for this invite code"
      );
      return;
    }

    redeemCode(trimmedCode);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 font-manrope backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-[#2A3441] dark:bg-[#111827]">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-[#1F2937]">
          <div className="flex items-center gap-2.5">
            <IoGiftOutline className="text-[20px] text-secondary_button" />
            <h3 className="text-[15px] font-bold text-[#13181B] dark:text-white">
              Enter Invite Code
            </h3>
          </div>
          <button
            type="button"
            onClick={() => setOpenInviteCode(false)}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <IoClose className="text-[22px]" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p className="text-[13px] leading-relaxed text-[#4E606E] dark:text-[#9CA3AF]">
            Enter your invite code to claim DeeXpoint rewards on your first
            deposit and trade.
          </p>

          <div className="relative mt-4">
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="ENTER INVITE CODE (E.G. DX-WELCOME500)"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-4 pr-11 text-[13px] font-medium uppercase tracking-wide text-[#13181B] outline-none placeholder:text-gray-400 focus:border-secondary_button focus:ring-1 focus:ring-secondary_button dark:border-[#2A3441] dark:bg-[#0B121D] dark:text-white dark:placeholder:text-[#6B7280]"
            />
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {showInputLoader && (
                <ClipLoader color="#0B75C2" size={18} />
              )}
              {showInputSuccess && (
                <FiCheckCircle className="text-[20px] text-[#26A17B]" />
              )}
            </div>
          </div>

          {previewData && (
            <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-[#1F2937] dark:bg-[#0B121D]">
              <div className="flex items-center gap-2">
                <IoGiftOutline className="text-[16px] text-secondary_button" />
                <h4 className="text-[13px] font-semibold text-[#13181B] dark:text-white">
                  Preview Rewards
                </h4>
              </div>
              <div className="mt-3 space-y-2.5">
                {[
                  {
                    label: "Deposit reward",
                    value: `${previewData.code.depositReward} pts`,
                  },
                  {
                    label: "Trade reward",
                    value: `${previewData.code.tradeReward} pts`,
                  },
                  {
                    label: "Min deposit",
                    value: `$${previewData.code.minDepositUSD}`,
                  },
                  {
                    label: "Min trade",
                    value: `$${previewData.code.minTradeUSD}`,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between text-[13px]"
                  >
                    <span className="text-[#4E606E] dark:text-[#9CA3AF]">
                      {item.label}
                    </span>
                    <span className="font-semibold text-[#13181B] dark:text-white">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {debouncedCode.length > 0 && !isFetching && !isTyping && isError && (
            <p className="mt-2 text-[12px] text-red-500">
              Could not preview this invite code. Please check and try again.
            </p>
          )}
        </div>

        <div className="border-t border-gray-100 px-5 py-4 dark:border-[#1F2937]">
          <button
            type="button"
            onClick={handleApply}
            disabled={!showInputSuccess || isRedeeming}
            className="flex h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[14px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRedeeming ? (
              <ClipLoader color="#ffffff" size={22} />
            ) : (
              "Apply Code"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteCodeModal;
